/**
 * @fileoverview Founding Ensemble cohort (Phase One) — shared client helpers.
 *
 * The cohort is a small, invitation-only group of instructors given the paid
 * tier free while they shape the product. Two rules live here so no surface has
 * to re-derive them:
 *
 * 1. **Membership is read from the server, never computed.** `founding_member`
 *    is a field on the account. A client that tried to infer it (from the
 *    grandfathering pin, say) would get the interesting case wrong — a member
 *    whose complimentary year has lapsed is STILL in the Founding
 *    Ensemble, because the badge is permanent and only the entitlement is
 *    dated.
 *
 * 2. **Absent reads as "not a member."** Unlike the kill-switch flags, whose
 *    absence must read as enabled, this one guards a badge and a private
 *    surface that nothing shipped before — so an older payload that predates
 *    the field means "no", never a badge somebody did not earn.
 */

/** Whether this account is in the founding cohort. */
export function isFoundingMember(user) {
  return user?.founding_member === true;
}

/**
 * The date the complimentary tier ends, or null.
 *
 * Deliberately reads the grandfathering pin rather than a founding-specific
 * field: the free year IS that pin (it already means "entitlement above what
 * this account pays for, until a stated date"), and a second field would be a
 * second answer to one question.
 */
export function complimentaryUntil(user) {
  if (!isFoundingMember(user) || !user?.grandfathered_until) return null;
  const date = new Date(user.grandfathered_until);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Whether the complimentary tier is still running. */
export function complimentaryIsLive(user) {
  const until = complimentaryUntil(user);
  return until ? until.getTime() > Date.now() : false;
}

/** "12 March 2027" — for the one line that tells a member when their year ends. */
export function formatComplimentaryUntil(user) {
  const until = complimentaryUntil(user);
  return until
    ? until.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : null;
}

/** Roughly-how-many-students bands, mirroring backend FOUNDING_STUDENT_BANDS. */
export const STUDENT_BANDS = ["1-10", "11-25", "26-50", "50+"];

/** Feedback categories, mirroring backend FEEDBACK_CATEGORIES. */
export const FEEDBACK_CATEGORIES = [
  { value: "idea", label: "An idea" },
  { value: "problem", label: "Something's wrong" },
  { value: "praise", label: "This worked well" },
  { value: "other", label: "Something else" },
];
